import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey } from "assets/jss/common-styles";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiDanger from "components/MuiExtras/Typography/MuiDanger";
import MuiInfo from "components/MuiExtras/Typography/MuiInfo";
import MuiSuccess from "components/MuiExtras/Typography/MuiSuccess";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import { CourseItemStatus, FeedType, PublicationType } from "models/ApiEnums";
import * as React from "react";
import { useTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////////

type BodyInfoData = {
    publicationType: PublicationType;

    permissions?: {
        hasAccess: boolean;
        canViewBody: boolean;
    };

    publication: {
        deleted: boolean;
        blocked: boolean;
        abuseReported?: boolean;
        inExpiredPeriod: boolean;
        inFuturePeriod: boolean;
        isInstitutional: boolean;
        courseItem?: {
            editStatus?: CourseItemStatus;
        };
    };
};

interface IProps {
    feedType: FeedType;
    data: BodyInfoData;
}

type Props = IProps & StyledComponentProps<FeedClassKey>;

/////////////////////////////////////////////////////////////////////////////////////

const StatusLine: React.FC<Props> = (props) => {

    const { t } = useTranslation();
    const { feedType, data: { publicationType, publication, permissions } } = props;

    if (feedType == FeedType.TaskDenouncement || feedType == FeedType.TaskForYourAction || feedType == FeedType.TaskForYourApproval)
        return null;

    if (feedType == FeedType.CourseItems) {
        const { editStatus } = publication.courseItem;
        if (editStatus == CourseItemStatus.Deleted)
            return <MuiDanger showIcon>{t("Common.Feed.Messages.CourseItemDeleted")}</MuiDanger>;
        if (editStatus == CourseItemStatus.MarkToDelete)
            return <MuiWarning showIcon>{t("Common.Feed.Messages.CourseItemMarkToDelete")}</MuiWarning>;
        if (editStatus == CourseItemStatus.MarkToInsert)
            return <MuiSuccess showIcon>{t("Common.Feed.Messages.CourseItemMarkToInsert")}</MuiSuccess>;
    }

    var content: JSX.Element =
        permissions?.hasAccess === false
            ? <MuiDanger showIcon>{t("Common.Feed.Messages.PublicationNoPermission")}</MuiDanger>
            : permissions?.canViewBody === false
                ? <MuiDanger showIcon>{t("Common.Feed.Messages.PublicationCannotViewBody")}</MuiDanger>
                : publication.deleted
                    ? <MuiDanger showIcon>{t("Common.Feed.Messages.PublicationDeleted")}</MuiDanger>
                    : publication.blocked && publication.abuseReported !== true
                        ? <MuiWarning showIcon>{t("Common.Feed.Messages.PublicationBlockedByManager")}</MuiWarning>
                        : publication.blocked && publication.abuseReported === true
                            ? <MuiWarning showIcon>{t("Common.Feed.Messages.PublicationBlockedByAbuseReport")}</MuiWarning>
                            : (publicationType != PublicationType.Poll && publicationType != PublicationType.Course && feedType != FeedType.CourseViewer && feedType != FeedType.CourseItems && publication.inExpiredPeriod)
                                ? <MuiDanger showIcon>{t("Common.Feed.Messages.PubInExpiredPeriod")}</MuiDanger>
                                : (publicationType != PublicationType.Poll && publicationType != PublicationType.Course && publication.inFuturePeriod)
                                    ? <MuiInfo showIcon>{t("Common.Feed.Messages.PubInFuturePeriod")}</MuiInfo>
                                    : (feedType == FeedType.PublicationAdmin && publication.abuseReported === true)
                                        ? <MuiWarning showIcon>{t("Common.Feed.Messages.PublicationDenounced")}</MuiWarning>
                                        : feedType == FeedType.PublicationAdmin && publication.isInstitutional
                                            ? <MuiInfo showIcon>{t("Common.Feed.Messages.Institutional")}</MuiInfo>
                                            : null;

    return content == null ? null : <MuiContainer margin="dense"> {content}</MuiContainer >;
};

export default StatusLine;
